import React, { useState, useContext, useEffect, useReducer, useRef } from 'react';
import * as THREE from 'three';
import { useThree, useFrame } from '@react-three/fiber';
import { Center, Grid, Sphere, useHelper } from '@react-three/drei';

import LayerMappingsEnum from '../enums/LayerMappingsEnum';
import { sceneUnitsFromMm } from '../utils/scaler';

import { StageTypeEnum } from '../store/viewerState/ViewerMode/ViewerMode'
import Stage2d from './Stage2d';
import Stage3d, {Stage3dProps} from './Stage3d';
import { useModelPropertiesStore } from '../store/modelPropertiesStore/modelPropertiesStore';
import { useViewerStateStore, ViewerLoadingStateEnum } from '../store/viewerStateStore'
interface GridProps {
    fadeDistance: number;
    position: THREE.Vector3;
    rotation: THREE.Euler;
}

interface BoundingBoxProps {
    position: THREE.Vector3;
    size: THREE.Vector3;
}

const StageManager = () => {

    const { viewerMode, setLoadingState } = useViewerStateStore();
    const [gridProps, setGridProps] = useState<GridProps>({ fadeDistance: 0, 
                                                            position: new THREE.Vector3(0, -1, 0),
                                                            rotation: new THREE.Euler(0,0,0)});
    const [bbProps, setBbProps] = useState<BoundingBoxProps>(   {   position: new THREE.Vector3(0, 0, 0), 
                                                                    size: new THREE.Vector3(1, 1, 1)});
    const [stage3dProps, setStage3dProps] = useState<Stage3dProps>({    center: new THREE.Vector3(0, 0, 0),
                                                                        isDebug: false,
                                                                        stageBounds: new THREE.Box3(  new THREE.Vector3(0, 0, 0), 
                                                                                                    new THREE.Vector3(1, 1, 1))});
    const gridRef = useRef<THREE.Mesh | null>(null);
    const {boundingBox, rotationOffset} = useModelPropertiesStore();
    
    const debugLayers = new THREE.Layers();
    debugLayers.enable(LayerMappingsEnum.DebugEntities);
    
    const stageLayers = new THREE.Layers();
    stageLayers.set(LayerMappingsEnum.Stage);
    
    const Layers2d = new THREE.Layers();
    Layers2d.set(LayerMappingsEnum.Elements2d);

    // Common properties for world grid and root product grid
    const commonGridProps = {
        infiniteGrid: true, 
        cellSize: sceneUnitsFromMm(1000), 
        sectionSize: sceneUnitsFromMm(10000), 
        fadeStrength: 5,
        followCamera: false
    };

    useEffect(() => {
        if (boundingBox) {
            setStage3dProps({...stage3dProps, center: boundingBox.getCenter(new THREE.Vector3)})
            const gridRotation = new THREE.Euler(Math.PI / 2, rotationOffset, 0);
            const mRot = new THREE.Matrix4().makeRotationFromEuler(gridRotation);
            //boundingBox.applyMatrix4(mRot);

            const gridPosition = boundingBox.getCenter(new THREE.Vector3());
            // Offset the grid slightly below the floor to prevent Z-fighting with floor coverings
            gridPosition.setZ(boundingBox.min.z - sceneUnitsFromMm(10));

            setGridProps(   {   fadeDistance: boundingBox.getSize(new THREE.Vector3()).length()*2, // Use length from corner to corner to get some margin
                                position: gridPosition,
                                rotation: gridRotation});

            setBbProps( {   position: boundingBox.getCenter(new THREE.Vector3()),
                            size: boundingBox.getSize(new THREE.Vector3())});

            setStage3dProps({
                                center: boundingBox.getCenter(new THREE.Vector3()),
                                stageBounds: boundingBox.clone()
                            });

            setLoadingState(ViewerLoadingStateEnum.Staged);
        }
    }, [boundingBox, rotationOffset])

   
    return (
        <>
        <Grid name="Grid" ref={gridRef} layers={stageLayers}
            {...commonGridProps}
            {...gridProps}
            
             /> 
     
        { // TODO: Replace with factory/selector if there is more than 2 stages
        viewerMode?.stageType === StageTypeEnum.Stage2d 
            ? <Stage2d />
            : <Stage3d {...stage3dProps} isDebug={false} />
        }
        
        </>
    );
};

export default StageManager;